import React from 'react';

import { BaseApiError } from '@lumapps/base-api';
import { getGroupAll } from '@lumapps/customer/ducks/selectors';
import { instanceIdSelector } from '@lumapps/instance/ducks/selectors';
import { track } from '@lumapps/metric/hooks/usePendo/track';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { usePlayUser } from '@lumapps/play-roles/hooks/usePlayUser';
import { isVideoSizeError } from '@lumapps/play-video/utils';
import { createVideo } from '@lumapps/play/api';
import { CreateVideoBody, CreateVideoResult, IsHiddenInValues, UploadingVideo } from '@lumapps/play/api/types';
import { ERROR_MAX_LENGTH_EXCEEDED } from '@lumapps/play/constants';
import { isPlayStandardEnabled } from '@lumapps/play/ducks/selectors';
import { PLAY } from '@lumapps/play/keys';
import { FileUploadFromDrive, UploadType } from '@lumapps/play/types';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate, DEFAULT_LANGUAGE } from '@lumapps/translations';
import { currentLanguageSelector, getLanguages } from '@lumapps/translations/ducks/selectors';

import { PLAY_LIBRARY } from '../../keys';
import { removeExtension } from '../../utils/removeExtension';

export interface UseCreateVideoParams {
    /** Set the video status by default and disable the button select */
    defaultStatus?: 'draft' | 'published';
    /** Upload Type file */
    type?: UploadType;
    /**
     * Set the video availability by default
     * @default { content: false, gallery: false, search: false }
     * */
    isHiddenIn?: IsHiddenInValues;
    /** Callback for clearing file data after failure */
    clearFileData: () => void;
    /** Callback for successfully creating a video */
    onSuccess: () => void;
    /** Set the error to be displayed in the upload dialog */
    setCreateVideoError?: (error: string) => void;
}

export const useCreateVideo = ({
    type = UploadType.video,
    isHiddenIn = { content: false, gallery: false, search: false },
    defaultStatus,
    onSuccess,
    setCreateVideoError,
    clearFileData,
}: UseCreateVideoParams) => {
    const [data, setData] = React.useState<CreateVideoResult | null>(null);

    const languages = useSelector(getLanguages);
    const currentLanguage = useSelector(currentLanguageSelector);
    const instanceId = useSelector(instanceIdSelector);
    const groupAll = useSelector(getGroupAll);
    const isPlayStandard = useSelector(isPlayStandardEnabled);

    const { maxUploadSize, humanReadableMaxUploadSize } = usePlayUser();
    const { translateAndReplace } = useTranslate();

    const defaultLanguage = DEFAULT_LANGUAGE;
    /**
     * The default language used for translatableTextField components. If the user current language is not available,
     * replace it with the default language
     */
    const translatableTextFieldDefaultLanguage = languages.includes(currentLanguage)
        ? currentLanguage
        : defaultLanguage;

    const { error: errorNotif } = useNotification();

    const createInitialVideo = React.useCallback(
        (fileName: string, driveFileUrl?: string, loop?: string) => {
            const initialTitle = removeExtension({ name: fileName });

            return {
                assetId: '',
                assets: [],
                description: {
                    lang: translatableTextFieldDefaultLanguage,
                    value: '',
                    translations: { [translatableTextFieldDefaultLanguage]: '' },
                },
                groupDetails: [{ id: groupAll.id, title: groupAll.name }],
                groupIds: [groupAll.id],
                editableByGroupIds: [],
                editableByGroupDetails: [],
                id: '',
                isDownloadable: false,
                isHiddenIn,
                language: translatableTextFieldDefaultLanguage,
                siteId: instanceId,
                status: defaultStatus || 'draft',
                publicationStatus: defaultStatus || 'draft',
                title: {
                    lang: translatableTextFieldDefaultLanguage,
                    value: initialTitle,
                    translations: { [translatableTextFieldDefaultLanguage]: initialTitle },
                },
                ...(type === UploadType.podcast && {
                    kind: UploadType.podcast,
                    loop: {
                        templateId: loop,
                    },
                }),
                ...(driveFileUrl && {
                    input: {
                        url: driveFileUrl,
                    },
                }),
            };
        },
        [defaultStatus, groupAll.id, groupAll.name, instanceId, isHiddenIn, translatableTextFieldDefaultLanguage, type],
    );

    const [createdVideo, setCreatedVideo] = React.useState<UploadingVideo | null>(null);

    const onCreateVideo = React.useCallback(
        async (file: File | FileUploadFromDrive, loop?: string) => {
            const initialVideo = createInitialVideo(file.name, 'sizeBytes' in file ? file.url : undefined, loop);

            try {
                // Let the backend decide if maxUploadSize is undefined
                if (isVideoSizeError('size' in file ? file?.size : file?.sizeBytes, maxUploadSize)) {
                    /** We want to track when a user is limited to gather data on play lite */
                    if (!isPlayStandard) {
                        track({
                            name: 'PLAY LITE | 1GB error',
                            props: {
                                action: '',
                            },
                        });
                    }
                    throw new Error(
                        translateAndReplace(
                            !isPlayStandard
                                ? PLAY_LIBRARY.LIMITED_UPLOAD_SIZE_UPGRADE
                                : PLAY_LIBRARY.LIMITED_UPLOAD_SIZE,
                            {
                                SIZE: humanReadableMaxUploadSize,
                            },
                        ),
                    );
                }

                const response = await createVideo(
                    initialVideo as CreateVideoBody,
                    'size' in file ? file?.size : file?.sizeBytes,
                );
                setData(response);
                setCreatedVideo({ ...initialVideo, id: response.videoId, assetId: response.assetId });
                onSuccess();
            } catch (error) {
                /** We need to clear the file data in case someone try to set a drive file then a regular file
                 *  The result would be that we don't trigger the onError callback but the videoFromDrive error instead
                 */
                clearFileData();

                /* When uploading a file from drive we send a notification instead of displaying the error */
                const displayError = (error: string) => {
                    if (file && setCreateVideoError && 'size' in file) {
                        setCreateVideoError(error);
                    } else {
                        errorNotif({ translate: error });
                    }
                };

                if (error instanceof BaseApiError) {
                    /** If the file comes from drive we handle the error in a toaster
                     * because we don't have any place to show the error */
                    if (error.response?.status === 429) {
                        displayError(PLAY.UPLOAD_DIALOG_CONSUMPTION_WARNING);
                    }

                    if (error.response?.data.code === ERROR_MAX_LENGTH_EXCEEDED) {
                        const errorMessage = translateAndReplace(
                            !isPlayStandard
                                ? PLAY_LIBRARY.LIMITED_UPLOAD_SIZE_UPGRADE
                                : PLAY_LIBRARY.LIMITED_UPLOAD_SIZE,
                            {
                                SIZE: humanReadableMaxUploadSize,
                            },
                        );

                        displayError(errorMessage);
                    }
                    return;
                }

                if (error instanceof Error) {
                    displayError(error.message);
                    return;
                }

                errorNotif({ translate: GLOBAL.GENERIC_ERROR });
            }
        },
        [
            clearFileData,
            createInitialVideo,
            errorNotif,
            humanReadableMaxUploadSize,
            isPlayStandard,
            maxUploadSize,
            onSuccess,
            setCreateVideoError,
            translateAndReplace,
        ],
    );

    return React.useMemo(
        () => ({
            /** Presigned URL to upload the media file */
            upload: data?.upload,
            /** Created video datas */
            createdVideo,
            /* createVideo */
            createVideo: onCreateVideo,
        }),
        [createdVideo, data?.upload, onCreateVideo],
    );
};
